/**
 * Client-side text layout helpers that mirror server/src/utils/textLayout.ts.
 */

export type FieldTextLayoutMode = 'centered' | 'form-line';

export interface FieldLayoutHint {
  boxHeight?: number;
  value?: string;
}

export function isSingleLineFormText(value?: string): boolean {
  const v = (value ?? '').trim();
  // Long or unbroken strings need a wider/taller box — not a single form-line slot.
  // Empty counts as candidate for initial text placement (compact box at click).
  return v.length <= 36 && !/[\r\n]/.test(v) && !/\S{28,}/.test(v);
}

export function fieldTextLayoutMode(
  fieldType: string,
  hint?: FieldLayoutHint
): FieldTextLayoutMode {
  if (fieldType === 'date' || fieldType === 'name' || fieldType === 'initials') {
    return 'form-line';
  }
  if (
    fieldType === 'text' &&
    isSingleLineFormText(hint?.value) &&
    (hint?.boxHeight ?? 56) <= 36
  ) {
    return 'form-line';
  }
  return 'centered';
}

const FORM_LINE_PAD_X = 2;
const FORM_LINE_BASELINE_FROM_BOTTOM_MIN = 2;

/** Mirrors server formLineBaselineOffsetFromBottom. */
export function formLineBaselineOffsetFromBottom(
  fontSize: number,
  fontFamily = 'helvetica'
): number {
  const glyphHeight = approximateFontHeight(fontFamily, fontSize);
  return Math.max(FORM_LINE_BASELINE_FROM_BOTTOM_MIN, glyphHeight * 0.22);
}

export interface TextLayoutOptions {
  lineSpacing?: number;
  verticalOffsetFactor?: number;
  fontFamily?: string;
  layoutMode?: FieldTextLayoutMode;
}

/**
 * Approximate font height for a given font family + size, matching pdf-lib's
 * heightAtSize = (Ascender - Descender)/1000 for the standard fonts we embed.
 */
export function approximateFontHeight(fontFamily: string, size: number): number {
  const lower = fontFamily.toLowerCase();
  if (lower.includes('times')) {
    // Times-Roman / Times-Italic: (683 + 217)/1000 ≈ 0.900
    return size * 0.9;
  }
  if (lower.includes('courier')) {
    // Courier: (629 + 157)/1000 ≈ 0.786
    return size * 0.786;
  }
  // Helvetica / Helvetica-Bold: (718 + 207)/1000 ≈ 0.925
  return size * 0.925;
}

/**
 * Horizontal start position for centered text (mirrors server centerLineX).
 */
export type TextAlignKey = 'left' | 'center' | 'right';

const FORM_LINE_PAD_X_PX = 2;

export function alignedLineX(
  lineWidth: number,
  boxLeft: number,
  boxWidth: number,
  align: TextAlignKey,
  layoutMode: FieldTextLayoutMode
): number {
  const pad = layoutMode === 'form-line' ? FORM_LINE_PAD_X_PX : 4;
  if (align === 'right') {
    return boxLeft + Math.max(pad, boxWidth - lineWidth - pad);
  }
  if (align === 'center') {
    return boxLeft + Math.max(pad, (boxWidth - lineWidth) / 2);
  }
  return boxLeft + pad;
}

export function centerLineXClient(
  lineWidth: number,
  boxLeft: number,
  boxWidth: number
): number {
  return alignedLineX(lineWidth, boxLeft, boxWidth, 'center', 'centered');
}

/**
 * Calculate the top position (in pixels, relative to the field box top)
 * where the first line of text should be placed for visual centering.
 *
 * This replicates the logic in the server's centeredBaselines.
 */
/** SVG/text baseline distance from top of field box (pixels). Mirrors server lineBaselines. */
/**
 * @param fontSize Font size in **PDF points** (not CSS pixels).
 * @param displayScale Multiply result by this for SVG/CSS pixel coordinates.
 */
export function getTextBaselineFromTop(
  lineCount: number,
  fontSize: number,
  boxHeight: number,
  options: TextLayoutOptions = {},
  displayScale = 1
): number {
  const {
    lineSpacing = 1.2,
    verticalOffsetFactor = 0.82,
    fontFamily = 'times',
    layoutMode = 'centered',
  } = options;

  if (layoutMode === 'form-line') {
    const offsetFromBottom = formLineBaselineOffsetFromBottom(fontSize, fontFamily);
    return Math.max(0, (boxHeight - offsetFromBottom) * displayScale);
  }

  const lineHeight = fontSize * lineSpacing;
  const blockHeight = lineCount * lineHeight;
  const glyphHeight = approximateFontHeight(fontFamily, fontSize);
  const firstBaselineFromBottom = (boxHeight + blockHeight) / 2 - glyphHeight * verticalOffsetFactor;
  return Math.max(0, (boxHeight - firstBaselineFromBottom) * displayScale);
}

/** @deprecated Use getTextBaselineFromTop */
export function getTextTopForCentering(
  lineCount: number,
  fontSize: number,
  boxHeight: number,
  options: TextLayoutOptions = {}
): number {
  return getTextBaselineFromTop(lineCount, fontSize, boxHeight, options);
}

export function getFormLineTextX(): number {
  return FORM_LINE_PAD_X;
}
